<template>
  <v-container fluid class="welcome-container pa-0 ma-0">
    <!-- Disclaimer Dialog -->
    <v-dialog v-model="showDisclaimer" max-width="600px" persistent>
      <v-card class="disclaimer-card">
        <v-icon class="close-icon" @click="showDisclaimer = false" size="32">
          mdi-close
        </v-icon>

        <v-card-text class="disclaimer-content pa-6">
          <h3 class="text-h6 mb-4 white--text">Important Notice</h3>
          <p class="mb-4">
            Cawthron Institute is making some changes to how we manage our free web apps.
            These apps make data from our research buoys (e.g. Tascam) accessible to the
            public. They're now hosted on C-State, a new web-enabled app which you'll need
            to sign up to, to access.
          </p>
          <p class="mb-4">
            Access to this app will remain free and your login data will be used solely
            for authentication purposes and to ensure secure access to our platform.
          </p>
          <p class="mb-0">
            We are not funded to maintain this tool for wider use, but we try to deliver
            the best possible service with a limited budget because we recognise its value
            to a variety of users. During this transitional period there are likely to be
            intermittent service issues which we are aware of. We are working hard to
            resolve remaining issues to provide consistent access for users. We appreciate
            your understanding.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>
    <v-img src="@/assets/Cawthron-buoy2.jpg" class="background-image" cover>
      <!-- Desktop Welcome Message -->
      <v-row justify="end" class="fill-height responsive-row d-none d-md-flex">
        <v-col cols="12" md="8" lg="6" class="welcome-msg">
          <h1>Welcome to the <span class="app-title">C-State</span> Buoy App</h1>

          <p class="subtitle-1 mb-5">
            This application provides near real-time oceanographic and atmospheric data
            from coastal buoys within Cawthron's Ocean Observing System.
            <v-tooltip bottom open-on-hover open-on-click>
              <template v-slot:activator="{ props }">
                <v-icon
                  color="primary"
                  v-bind="props"
                  style="cursor: pointer; margin-left: 8px"
                >
                  mdi-alert-circle-outline
                </v-icon>
              </template>
              <div class="tooltip-content">
                Data is currently limited to waves, winds (and other meteorological
                variables), water temperature, and location of these buoys.
              </div>
            </v-tooltip>
          </p>
          <p class="subtitle-1 mb-5">
            Log in to access publicly available buoys. If you have private deployments,
            you will be able to see them too.
          </p>
          <v-btn color="primary" class="mb-4" @click="showDisclaimer = true">
            <v-icon start>mdi-information</v-icon>
            Important Notice
          </v-btn>
          <v-btn color="primary" large @click="login">Access the App</v-btn>
        </v-col>
      </v-row>

      <!-- Mobile Container -->
      <div class="mobile-container d-md-none">
        <!-- Top welcome message -->
        <div class="mobile-welcome">
          <h1>Welcome to the <span class="app-title">C-State</span> Buoy App</h1>
        </div>

        <!-- Bottom access button -->
        <div class="mobile-access">
          <v-btn color="primary" @click="showDisclaimer = true">
            <v-icon start>mdi-information</v-icon>
            Important Notice
          </v-btn>
          <v-btn color="primary" class="mt-1" @click="login">Access the App</v-btn>
        </div>

        <!-- Info Buttons -->
        <div :class="['info-buttons', { 'bottom-buttons': isLandscape }]">
          <v-btn
            v-for="(item, index) in infoItems"
            :key="index"
            class="info-button mb-2"
            color="primary"
            @click="openDialog(index)"
          >
            {{ item.title }}
          </v-btn>
        </div>
      </div>

      <!-- Mobile Dialogs -->
      <v-dialog
        v-model="activeDialog"
        max-width="90%"
        transition="dialog-bottom-transition"
      >
        <v-card v-if="activeDialogContent">
          <v-card-title>{{ activeDialogContent.title }}</v-card-title>
          <v-card-text>{{ activeDialogContent.content }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" @click="activeDialog = false">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-img>
  </v-container>
</template>

<script>
import { useAuth0 } from "@auth0/auth0-vue";

export default {
  name: "WelcomePage",
  setup() {
    const { loginWithRedirect } = useAuth0();

    const login = () => {
      console.log(`Login from WelcomePage with simplified configuration`);
      loginWithRedirect({
        authorizationParams: {
          scope: "openid profile email",
          redirect_uri: window.location.origin,
        },
      });
    };

    return {
      login,
    };
  },
  data() {
    return {
      showDisclaimer: false,
      activeDialog: false,
      activeDialogContent: null,
      isLandscape: false,
      infoItems: [
        {
          title: "About the App",
          content:
            "This application provides near real-time oceanographic and atmospheric data from coastal buoys within Cawthron's Ocean Observing System. Data is currently limited to waves, winds (and other meteorological variables), water temperature, and location of these buoys.",
        },
        {
          title: "Why Sign In?",
          content:
            "Logging in gives you access to publicly available buoys. If you have private deployments, you will be able to see them too after authentication.",
        },
      ],
    };
  },
  mounted() {
    this.checkOrientation();
    window.addEventListener("resize", this.checkOrientation);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.checkOrientation);
  },
  methods: {
    openDialog(index) {
      this.activeDialogContent = this.infoItems[index];
      this.activeDialog = true;
    },
    checkOrientation() {
      this.isLandscape = window.innerWidth > window.innerHeight;
    },
  },
};
</script>

<style scoped>
.welcome-container {
  height: 100vh;
}

.mobile-container {
  height: 100vh;
  width: 100%;
}

.mobile-welcome {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
  color: white;
  margin-top: 2rem;
  width: 90%;
}

.mobile-welcome h2 {
  font-size: 1.5rem;
  font-weight: bold;
}

.close-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  cursor: pointer;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 4px;
}

.mobile-access {
  position: absolute;
  bottom: 6rem;
  left: 50%;
  transform: translateX(-50%);
  width: 90%;
  text-align: center;
}

.welcome-msg {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  color: white;
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(221, 214, 214, 0.279);
  backdrop-filter: blur(5px);
  border-radius: 15px;
  max-width: 30vw;
  margin-right: 4rem;
  max-height: fit-content;
  min-height: 600px;
}

/* Info buttons styling for mobile */
.info-buttons {
  position: fixed;
  right: 0;
  top: 55%;
  transform: translateY(-50%);
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  z-index: 1;
}

.bottom-buttons {
  flex-direction: row;
  bottom: 0;
  top: auto;
  right: 50%;
  transform: translateX(50%);
  padding-bottom: 16px;
}

.info-button {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  border-radius: 8px 0 0 8px !important;
  height: auto !important;
  padding: 16px 8px !important;
}

.bottom-buttons .info-button {
  writing-mode: horizontal-tb;
  transform: none;
  border-radius: 8px !important;
  height: 48px !important;
}

.app-title {
  color: white;
}

.background-image {
  height: 100%;
  width: 100%;
}

.fill-height {
  height: 50%;
}

h1 {
  font-weight: bold;
  color: #1976d2;
}

.subtitle-1 {
  font-size: 1.2rem;
}

.v-btn {
  max-width: 50%;
  min-width: fit-content;
  min-height: fit-content;
}

.tooltip-content {
  color: white;
  padding: 10px;
  border-radius: 5px;
  max-width: 250px;
  font-size: 0.9rem;
}

/* Desktop media queries */
@media (min-width: 1904px) {
  .welcome-msg {
    max-width: 25vw;
    margin-right: 6rem;
  }

  h1 {
    font-size: 4rem;
  }

  .subtitle-1 {
    font-size: 1.5rem;
  }

  .v-btn {
    font-size: 1.2rem;
    padding: 12px 24px;
    max-width: fit-content;
  }
}

@media (min-width: 2560px) {
  .welcome-msg {
    max-width: 30vw;
    margin-right: 8rem;
  }

  h1 {
    font-size: 5rem;
  }

  .subtitle-1 {
    font-size: 1.75rem;
  }

  .v-btn {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    font-size: 1.5rem;
    padding: 2rem;
    margin-bottom: 1rem;
  }
}

/* Mobile and landscape mode styles */
@media (max-width: 900px) and (orientation: landscape) {
  .mobile-container {
    display: flex;
    flex-direction: column;
    height: auto;
    min-height: 100vh;
    position: relative;
    padding: 1rem;
  }

  .mobile-welcome {
    position: relative;
    transform: none;
    left: auto;
    margin: 0.5rem auto;
    width: 100%;
    text-align: center;
    background-color: transparent;
  }

  .mobile-welcome h1 {
    font-size: 1.5rem;
    margin: 0;
  }

  .mobile-access {
    position: relative;
    transform: none;
    left: auto;
    bottom: auto;
    margin: 0.5rem auto;
    width: auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;
  }

  .mobile-access .v-btn {
    font-size: 0.9rem;
    height: 36px !important;
    padding: 0 1rem !important;
    writing-mode: horizontal-tb;
    transform: none;
    width: auto;
  }

  .info-buttons {
    margin-bottom: 1rem;
  }

  .bottom-buttons {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.4);
    margin: 0;
    width: 100%;
  }

  .bottom-buttons .info-button {
    writing-mode: horizontal-tb;
    transform: none;
    height: 32px !important;
    padding: 0 0.75rem !important;
    margin: 0 !important;
    font-size: 0.9rem;
    min-width: auto !important;
    width: auto !important;
  }

  .d-none.d-sm-flex {
    display: none !important;
  }
}

/* Additional helper for very short heights */
@media (max-height: 400px) and (orientation: landscape) {
  .mobile-welcome h1 {
    font-size: 1.2rem;
  }

  .mobile-access .v-btn,
  .bottom-buttons .info-button {
    height: 28px !important;
    font-size: 0.8rem;
  }

  .bottom-buttons {
    padding: 0.25rem;
  }
}
</style>
